<template>
  <li
    class="todo-list-item"
    :class="{ 'text-quaternary line-through': state.isDeleted || state.isCompleted }"
    @click="taskClicked(task)"
  >
    <div class="flex mr-3 items-center text-md w-full gap-4">
      <Checkbox
        v-model="state.isCompleted"
        @click.stop
        @change="toggleComplete()"
      />
      <div class="flex items-start flex-grow">
        <div>
          <div class="leading-[1.1]">
            <Icon
              v-if="state.isDeleted"
              v-tippy="'Deleted'"
              type="trash"
              class="mr-1 text-sm"
            />
            {{ state.title }}
            <Icon
              v-if="state.isStarred"
              type="star"
              class="text-warning ml-1"
            />
          </div>
          <div
            v-if="descriptionLength"
            class="text-tertiary text-sm font-light"
          >
            {{ truncatedDescription }}
          </div>
          <div
            v-if="state.dueDate"
            class="flex items-center gap-1 mt-1"
            :class="[
              dayjs(state.dueDate).isToday() || dayjs(state.dueDate).isBefore() ? 'text-error' : 'text-tertiary'
            ]"
          >
            <Icon
              type="calendar"
              class="text-sm"
            />
            <div
              v-if="!state.isDeleted && !state.isCompleted"
              v-tippy="{ content: dayjs(state.dueDate).format('DD/MM/YYYY HH:mm') }"
            >
              <div
                v-if="dayjs(state.dueDate).isToday()"
                class="mr-3 text-error text-sm whitespace-nowrap"
              >
                Today
              </div>
              <div
                v-else
                class="mr-3 text-sm whitespace-nowrap flex items-center"
                :class="{ 'text-error': dayjs(state.dueDate).isBefore() }"
              >
                {{ dayjs(state.dueDate).format('h:mma') }}
                <Icon
                  type="circle"
                  class="text-[6px] mx-1"
                />
                {{ dayjs(state.dueDate).format('MMM D') }}
              </div>
            </div>
            <div
              v-else
              v-tippy="{ content: dayjs(state.dueDate).format('DD/MM/YYYY HH:mm') }"
              class="mr-3 text-sm whitespace-nowrap flex items-center"
            >
              {{ dayjs(state.dueDate).format('h:mma') }}
              <Icon
                type="circle"
                class="text-[6px] mx-1"
              />
              {{ dayjs(state.dueDate).format('MMM D') }}
            </div>
          </div>
        </div>
        <router-link
          v-if="chips.indexOf('link') !== -1 && state.linkType === 'agreement'"
          :to="'/agreement/' + state.linkId"
        >
          <Chip
            class="ml-5"
            size="sm"
          >
            <Icon type="link" />
            {{ state.linkTitle }}
          </Chip>
        </router-link>

        <Chip
          v-if="chips.indexOf('createdBy') !== -1 && state.createdBy != lsUserID"
          class="ml-5"
        >
          Created By {{ state.createdBy }}
        </Chip>
      </div>
      <div class="task-item-actions">
        <Button
          v-tippy="'Edit Task'"
          icon="pencil"
          plain
          severity="secondary"
        />
        <Button
          v-if="!state.isDeleted"
          v-tippy="'Delete Task'"
          icon="trash"
          plain
          severity="secondary"
          @click.stop="toggleDelete()"
        />
        <Button
          v-else
          v-tippy="'Restore Task'"
          icon="trash-undo"
          plain
          severity="secondary"
          @click.stop="toggleDelete()"
        />
      </div>
    </div>
  </li>
</template>

<script setup>
// vue
import { ref, watch, computed } from 'vue'

// components
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import Button from '@/components/button/Button.vue'

// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useTaskStore } from '@/stores/TaskStore.ts'

// utilities
import { ellipsize } from '@/utilities'

const props = defineProps({
  task: {
    type: Object,
    default: () => {},
    required: true
  },
  chips: {
    type: Array,
    default: () => ['link']
  },
  descriptionLength: {
    type: Number,
    default: 100
  }
})
const emit = defineEmits(['updateTask', 'toggleCompleted', 'taskClicked', 'deleteTask'])
const authStore = useAuthStore()
const taskStore = useTaskStore()
const lsUserID = authStore.details.id

const state = ref({ ...props.task })

// Watch for changes in the task prop and update the state
watch(
  () => props.task,
  newTask => {
    state.value = { ...newTask }
  },
  { deep: true }
)

async function toggleComplete() {
  const updatedTask = await taskStore.toggleComplete(state.value.id, state.value.isCompleted)
  if (updatedTask) {
    state.value = { ...updatedTask }
    emit('toggleCompleted', updatedTask)
    emit('updateTask', updatedTask)
  }
}

async function toggleDelete() {
  const newDeletedStatus = !state.value.isDeleted
  state.value.isDeleted = newDeletedStatus

  const updatedTask = await taskStore.toggleDelete(state.value.id, newDeletedStatus)
  if (updatedTask) {
    state.value = { ...updatedTask }
    emit('deleteTask', updatedTask)
    emit('updateTask', updatedTask)
  } else {
    // Revert on error
    state.value.isDeleted = !newDeletedStatus
  }
}

function taskClicked(task) {
  emit('taskClicked', task)
}

const truncatedDescription = computed(() => {
  if (!state.value.description) {
    return ''
  }
  if (props.descriptionLength && state.value.description.length > props.descriptionLength) {
    return ellipsize(state.value.description, props.descriptionLength)
  }
  return state.value.description
})
</script>

<style>
.todo-list-item {
  padding: var(--s-3) var(--s-4);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-secondary);
  box-sizing: border-box;
  /* Ensure padding is included in width calculation */
  margin-bottom: var(--s-4);
}

.todo-list-item:hover {
  background-color: var(--bg-primary_hover);
}

.task-item-actions {
  visibility: hidden;
  display: flex;
  gap: 0.25rem;
}

.todo-list-item:hover .task-item-actions {
  visibility: visible;
}

/* Remove the last-child border style since we're using cards now */
.todo-list-item:last-child {
  margin-bottom: 0;
}
</style>
